/*
 * Purpose: Tenant-scoped workflow schedule persistence + due checks
 * Last modified: 2026-06-28 | Agent
 * Completeness: 90
 */
import { tenantStorageKey } from '@/lib/tenantStorage';

export type ScheduleFrequency = 'once' | 'hourly' | 'daily' | 'weekly' | 'monthly';

export interface WorkflowSchedule {
  id: string;
  workflowId: string;
  workflowName: string;
  frequency: ScheduleFrequency;
  time: string;
  enabled: boolean;
  lastRunAt?: string;
  nextRunAt?: string;
}

const STORAGE_SUFFIX = 'workflow_schedules';

export function schedulesStorageKey(): string {
  return tenantStorageKey(STORAGE_SUFFIX);
}

export function loadSchedules(): WorkflowSchedule[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(schedulesStorageKey());
    if (!raw) return [];
    const parsed = JSON.parse(raw) as WorkflowSchedule[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveSchedules(schedules: WorkflowSchedule[]): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(schedulesStorageKey(), JSON.stringify(schedules));
}

function parseTime(time: string): { hour: number; minute: number } {
  const [h, m] = time.split(':').map(Number);
  return { hour: h || 0, minute: m || 0 };
}

function sameMinute(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate() &&
    a.getHours() === b.getHours() &&
    a.getMinutes() === b.getMinutes()
  );
}

export function isScheduleDue(schedule: WorkflowSchedule, now = new Date()): boolean {
  if (!schedule.enabled) return false;

  if (schedule.lastRunAt) {
    const last = new Date(schedule.lastRunAt);
    if (sameMinute(last, now)) return false;
  }

  const { hour, minute } = parseTime(schedule.time);
  if (now.getHours() !== hour || now.getMinutes() !== minute) return false;

  switch (schedule.frequency) {
    case 'hourly':
      return true;
    case 'daily':
      return true;
    case 'weekly':
      return now.getDay() === 1;
    case 'monthly':
      return now.getDate() === 1;
    case 'once':
      return !schedule.lastRunAt;
    default:
      return false;
  }
}

export function markScheduleRan(schedule: WorkflowSchedule): WorkflowSchedule {
  return { ...schedule, lastRunAt: new Date().toISOString() };
}
